import type {
  AmhsFinalSimulationLeg,
  BrowserKinetic,
  SimulationAssignment,
  TransportRequest,
} from '../types/simulationPlayback';
import {
  buildTransportRequestActivityWindows,
  isTransportRequestActiveAtTime,
  type TransportRequestActivityWindow,
} from './simulationPlayback';
import { isTravelPhase } from './simulationPhaseUi';

export type TrPanelTab = 'active' | 'pending' | 'completed';

/** Live row status shown in the TRANSPORT REQUESTS table. */
export type TrLiveStatus = 'IN TRANSIT' | 'PICKUP' | 'WAITING' | 'PENDING' | 'COMPLETED';

export type TransportRequestPanelRow = {
  id: string;
  requestId: string;
  status: TrLiveStatus;
  assignedOht: string;
  fromTo: string;
  tab: TrPanelTab;
  startSec: number;
  endSec: number;
};

const apiLegsByRequestCache = new WeakMap<
  AmhsFinalSimulationLeg[],
  Map<string, AmhsFinalSimulationLeg[]>
>();

function indexApiLegsByRequest(
  legs: AmhsFinalSimulationLeg[]
): Map<string, AmhsFinalSimulationLeg[]> {
  const cached = apiLegsByRequestCache.get(legs);
  if (cached) return cached;
  const map = new Map<string, AmhsFinalSimulationLeg[]>();
  for (const leg of legs) {
    const list = map.get(leg.requestId);
    if (list) list.push(leg);
    else map.set(leg.requestId, [leg]);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);
  }
  apiLegsByRequestCache.set(legs, map);
  return map;
}

function rankRequestLeg(a: AmhsFinalSimulationLeg, b: AmhsFinalSimulationLeg): number {
  const aTravel = isTravelPhase(a) ? 0 : 1;
  const bTravel = isTravelPhase(b) ? 0 : 1;
  if (aTravel !== bTravel) return aTravel - bTravel;
  if (a.startSec !== b.startSec) return a.startSec - b.startSec;
  return b.endSec - b.startSec - (a.endSec - a.startSec);
}

/** Active API leg for a transport request at time `t`. */
export function findActiveApiLegForRequest(
  legs: AmhsFinalSimulationLeg[],
  requestId: string,
  t: number
): AmhsFinalSimulationLeg | null {
  const group = indexApiLegsByRequest(legs).get(requestId);
  if (!group?.length) return null;
  let best: AmhsFinalSimulationLeg | null = null;
  for (const leg of group) {
    if (leg.startSec > t) break;
    if (t > leg.endSec) continue;
    if (!best || rankRequestLeg(leg, best) < 0) best = leg;
  }
  return best;
}

export function assignedOhtFromRequest(request: TransportRequest): string {
  const raw = request.assignedOHT ?? request.assignedOht;
  const value = typeof raw === 'string' ? raw.trim() : '';
  return value || '—';
}

function formatEndpoint(node: string, port?: string): string {
  const base = (node || '—').trim() || '—';
  const p = port?.trim();
  if (!p || p === base) return base;
  return `${base}(${p})`;
}

export function formatTrFromTo(request: TransportRequest): string {
  const from = formatEndpoint(request.pickup, request.pickupPort);
  const to = formatEndpoint(request.drop, request.dropPort);
  return `${from} -> ${to}`;
}

function isStationaryApiLeg(leg: AmhsFinalSimulationLeg): boolean {
  return (leg.segmentIds?.length ?? 0) === 0 && (leg.segmentWindows?.length ?? 0) === 0;
}

/**
 * Map the active leg phase to the dashboard status chip.
 * Deadhead / pre-pickup dwell → PICKUP; loaded delivery → IN TRANSIT; waits → WAITING.
 */
export function liveStatusFromLeg(leg: AmhsFinalSimulationLeg | null): TrLiveStatus {
  if (!leg) return 'WAITING';
  const phase = (leg.phase ?? '').toLowerCase();
  if (phase === 'process_wait') return 'WAITING';
  if (phase === 'deadhead' || phase === 'return_home') return 'PICKUP';
  if (phase === 'delivery') {
    if (isStationaryApiLeg(leg)) return 'PICKUP';
    return 'IN TRANSIT';
  }
  if (isStationaryApiLeg(leg) || (leg.waitS ?? 0) > 0) return 'WAITING';
  if (isTravelPhase(leg)) return 'IN TRANSIT';
  return 'WAITING';
}

function windowForRequest(
  requestId: string,
  windows: Map<string, TransportRequestActivityWindow>,
  assignments: Map<string, SimulationAssignment>
): TransportRequestActivityWindow | undefined {
  const existing = windows.get(requestId);
  if (existing) return existing;
  const assignment = assignments.get(requestId);
  if (!assignment) return undefined;
  return {
    startSec: assignment.plannedStartSec,
    endSec: assignment.plannedStartSec + assignment.totalCostS,
  };
}

export function buildTransportRequestPanelRows(args: {
  transportRequests: TransportRequest[];
  assignments: SimulationAssignment[];
  browserKinetic: BrowserKinetic | null;
  simulationTime: number;
}): TransportRequestPanelRow[] {
  const { transportRequests, assignments, browserKinetic, simulationTime } = args;
  const windows = buildTransportRequestActivityWindows(browserKinetic, assignments);
  const assignmentMap = new Map(assignments.map((a) => [a.requestId, a]));
  const apiLegs = browserKinetic?.apiLegs ?? [];

  const rows: TransportRequestPanelRow[] = [];
  for (const request of transportRequests) {
    const requestId = request.requestId || request.transportRequestId;
    if (!requestId) continue;
    const window = windowForRequest(requestId, windows, assignmentMap);
    const id = request.transportRequestId || requestId;
    const assignedOht = assignedOhtFromRequest(request);
    const fromTo = formatTrFromTo(request);
    const startSec = window?.startSec ?? request.arrivalTimeSec ?? 0;
    const endSec = window?.endSec ?? startSec;

    let tab: TrPanelTab;
    let status: TrLiveStatus;

    if (!window || simulationTime < window.startSec) {
      tab = 'pending';
      status = 'PENDING';
    } else if (!isTransportRequestActiveAtTime(simulationTime, window)) {
      tab = 'completed';
      status = 'COMPLETED';
    } else {
      tab = 'active';
      status = liveStatusFromLeg(
        findActiveApiLegForRequest(apiLegs, requestId, simulationTime)
      );
    }

    rows.push({ id, requestId, status, assignedOht, fromTo, tab, startSec, endSec });
  }

  rows.sort((a, b) => a.startSec - b.startSec || a.id.localeCompare(b.id));
  return rows;
}

export function countTrPanelTabs(rows: TransportRequestPanelRow[]): Record<TrPanelTab, number> {
  const counts: Record<TrPanelTab, number> = { active: 0, pending: 0, completed: 0 };
  for (const row of rows) counts[row.tab] += 1;
  return counts;
}
